import { Box } from '@mui/material';
import { type ComponentProps, lazy, Suspense } from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AddWatchList } from '@/pages/ValuationsV2/components/AssetDetails/AddWatchList';
import { AssetBadges } from '@/pages/ValuationsV2/components/AssetDetails/AssetBadges';
import { AssetDetailsHeader } from '@/pages/ValuationsV2/components/AssetDetails/AssetDetailsHeader';
import { LazyLoadedMap } from '@/pages/ValuationsV2/components/AssetDetails/LazyLoadedMap';
import { combineSx } from '@/utils/combineSx';
import { pxToRem } from '@/utils/pxToRem';
import { Col, ColGroup } from '../Col';
import { SUMMARY_ITEMS } from './constants';
import { FinancialConsiderationsView } from './FinancialConsiderationsView';
import { KeyInformationView } from './KeyInformationView';
import { LeadAnalystView } from './LeadAnalystView';
import { ProductionAndReservesView } from './ProductionAndReservesView';
import { TimelineView } from './TimelineView';

type AreaColOptions = {
  align: 'start' | 'end';
};

type AreaCol<TKeys extends string = string> = {
  options?: AreaColOptions;
} & {
  [K in Exclude<TKeys | '_', 'options'>]?: number;
};

const getCols = (colsNumber: number, row: AreaCol) => {
  const { options, ...counts } = row;

  const areas = Object.entries(counts).flatMap(([area, count]) => {
    if (typeof count !== 'number') return [];
    const safeCount = count > 0 ? Math.floor(count) : 0;
    return Array(safeCount).fill(area);
  });

  const deficit = Math.max(0, colsNumber - areas.length);
  const padding = Array(deficit).fill('.');
  const aligned = options?.align === 'end' ? [...padding, ...areas] : [...areas, ...padding];

  return aligned.slice(0, colsNumber).join(' ');
};

const getGridArea = <TKeys extends string>(colsNumber: number, rows: AreaCol<TKeys>[]) => {
  return rows.map(row => `"${getCols(colsNumber, row)}"`).join(' ');
};

enum GridArea {
  Actions = 'actions',
  LeadAnalyst = 'lead-analyst',
  Timeline = 'timeline',
  KeyInformation = 'key-information',
  FinancialConsiderations = 'financial-considerations',
  ProductionReserves = 'production-reserves',
  AssetReport = 'asset-report',
  Map = 'map',
  AssetName = 'assetName',
}

const XS: AreaCol<GridArea>[] = [
  { [GridArea.AssetName]: 1 },
  { [GridArea.Map]: 1 },
  { [GridArea.LeadAnalyst]: 1 },
  { [GridArea.Actions]: 1 },
  { [GridArea.Timeline]: 1 },
  { [GridArea.KeyInformation]: 1 },
  { [GridArea.FinancialConsiderations]: 1 },
  { [GridArea.ProductionReserves]: 1 },
  { [GridArea.AssetReport]: 1 },
];

const LG: AreaCol<GridArea>[] = [
  {
    [GridArea.AssetName]: 14,
    [GridArea.Map]: 10,
  },
  {
    [GridArea.Actions]: 5,
    [GridArea.KeyInformation]: 19,
  },

  {
    [GridArea.Timeline]: 18,
    [GridArea.LeadAnalyst]: 6,
  },
  {
    [GridArea.ProductionReserves]: 16,
    [GridArea.FinancialConsiderations]: 8,
  },
  { [GridArea.AssetReport]: 24 },
];

enum COLS_COUNT {
  LG = 24,
  XS = 1,
}

const summaryStyles = {
  container: {
    display: 'grid',
    gap: 'var(--gap-section)',
    padding: '0.25rem',
    gridTemplateRows: {
      xs: `repeat(${XS.length - 1}, min-content)`,
      lg: `repeat(${LG.length - 1}, min-content)`,
    },
    gridAutoRows: {
      xs: 'min-content',
      lg: '1fr',
    },
    gridAutoFlow: 'row',
    gridTemplateColumns: {
      xs: `repeat(${COLS_COUNT.XS}, 1fr)`,
      lg: `repeat(${COLS_COUNT.LG}, 1fr)`,
    },
    gridTemplateAreas: {
      xs: getGridArea<GridArea>(COLS_COUNT.XS, XS),
      lg: getGridArea<GridArea>(COLS_COUNT.LG, LG),
    },
  },
};

type AssetSummaryProps = {
  children: React.ReactNode;
};

const LazyDownloads = lazy(() =>
  import('./Downloads').then(module => ({ default: module.Downloads })),
);

const ActionsContainer = (props: ComponentProps<typeof Box>) => {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <LazyDownloads {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

const AssetSummary = ({ children }: AssetSummaryProps) => {
  return (
    <ErrorBoundary>
      <Box sx={combineSx(summaryStyles.container, {})} data-id='asset-summary'>
        <Col<GridArea>
          gridArea={GridArea.AssetName}
          data-id='asset-summary-asset-name'
          sx={{
            gridTemplateRows: '1fr',
            padding: '0.15rem 0.5rem 0.15rem 0.15rem',
            background: 'transparent',
            minHeight: '8rem',
          }}
        >
          <Box
            display='flex'
            alignItems='flex-start'
            flexDirection='column'
            width='100%'
            height='100%'
            data-id='header-layout-root'
          >
            <AssetBadges />
            <AssetDetailsHeader />
          </Box>
        </Col>

        <ColGroup<GridArea>
          gridArea={GridArea.Actions}
          sx={{
            gridTemplateAreas: `
              'col-1'
              'col-2'
            `,
          }}
        >
          <Col<'col-1'>
            columnTitle={SUMMARY_ITEMS.DOWNLOADS}
            data-id='asset-summary-actions'
            gridArea='col-1'
          >
            <ActionsContainer />
          </Col>

          <Col<'col-2'>
            sx={{
              gridTemplateRows: '1fr',
              padding: '0',
              height: pxToRem(36),
            }}
            gridArea='col-2'
            data-id='asset-summary-watchlist'
          >
            <AddWatchList />
          </Col>
        </ColGroup>

        <Col<GridArea>
          gridArea={GridArea.Map}
          data-id='asset-summary-map'
          sx={{
            gridTemplateRows: '1fr',
            minHeight: '11rem',
            padding: '0.15rem',
          }}
        >
          <LazyLoadedMap />
        </Col>

        <Col<GridArea>
          columnTitle={SUMMARY_ITEMS.TIMELINE}
          data-id='asset-summary-timeline'
          gridArea={GridArea.Timeline}
        >
          <TimelineView />
        </Col>

        <Col<GridArea>
          key={GridArea.KeyInformation}
          columnTitle={SUMMARY_ITEMS.KEY_INFORMATION}
          gridArea={GridArea.KeyInformation}
        >
          <KeyInformationView
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(auto-fit, minmax(10rem, 1fr))',
            }}
          />
        </Col>

        <Col<GridArea>
          gridArea={GridArea.LeadAnalyst}
          variant='highlighted'
          columnTitle={SUMMARY_ITEMS.LEAD_ANALYST}
          data-id='asset-summary-lead-analyst'
          sx={{
            gap: '0.25rem',
          }}
        >
          <LeadAnalystView />
        </Col>

        <Col<GridArea>
          key={GridArea.ProductionReserves}
          data-id='asset-summary-production-and-reserves'
          columnTitle={SUMMARY_ITEMS.PRODUCTION_AND_RESERVES}
          gridArea={GridArea.ProductionReserves}
        >
          <ProductionAndReservesView
            gridTemplateColumns={{
              xs: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(auto-fit, minmax(10rem, 1fr))',
            }}
          />
        </Col>

        <Col<GridArea>
          key={GridArea.FinancialConsiderations}
          data-id='asset-summary-financial-considerations'
          columnTitle={SUMMARY_ITEMS.FINANCIAL_CONSIDERATIONS}
          gridArea={GridArea.FinancialConsiderations}
        >
          <FinancialConsiderationsView
            gridTemplateRows='repeat(3, min-content)'
            gridTemplateColumns='1fr'
          />
        </Col>

        <Col<GridArea> columnTitle='Asset Report' gridArea={GridArea.AssetReport}>
          {children}
        </Col>
      </Box>
    </ErrorBoundary>
  );
};

export { AssetSummary };
